import { Button, Checkbox, Heading, Icon, IconButton, Input, InputGroup, InputLeftElement, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";

import { FaArrowLeft, FaArrowRight, FaCloudUploadAlt, FaFile, FaFilter, FaSearch, FaSync } from "react-icons/fa";
import { RiUploadCloudFill } from "react-icons/ri";
import { GET } from "../../utils/ApiRequestProvider";
import AddDocument from "../../components/Modals/AddDocument";

export default function UploadHistory() {
    const toast = useToast();
    const [items, setItems] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(false);
    const [paginationQuery, setPaginationQuery] = useState({ page: 1, pageSize: 10 });
    const [pagination, setPagination] = useState({ currentPage: 0, totalPages: 0, totalRecords: 0 });
    const [search, setSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return items.filter((it) => {
            if (statusFilter && it.status !== statusFilter) return false;
            if (!term) return true;
            return (
                String(it.id ?? '').includes(term) ||
                (it.filename ?? '').toLowerCase().includes(term) ||
                (it.docType ?? '').toLowerCase().includes(term)
            );
        });
    }, [items, search, statusFilter]);

    const allChecked = filtered.length > 0 && filtered.every((i) => selected.has(String(i.id)));
    const someChecked = filtered.some((i) => selected.has(String(i.id)));
    const toggleAll = () => {
        if (allChecked) setSelected(new Set());
        else setSelected(new Set(filtered.map((i) => String(i.id))));
    };
    const toggleOne = (id: string) => {
        setSelected((p) => {
            const n = new Set(p);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const convertDate = (date: string | null) => (date ? new Date(date).toLocaleString() : '');

    async function FetchList() {
        setLoading(true);
        try {
            const res = await GET(`/api/internal/upload-history?page=${paginationQuery.page}&pageSize=${paginationQuery.pageSize}`);
            setItems(res.data.documents ?? []);
            setPagination(res.data.pagination);
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error', isClosable: false, duration: 1000, position: 'top-right'
            });
        }
        setLoading(false);
    }

    useEffect(() => {
        FetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationQuery]);

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <AddDocument
                isOpen={isUploadOpen}
                onClose={onUploadClose}
                DocType={'Purchase Invoice'}
                RefreshList={FetchList}
            />
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'row' }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 0 }}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={RiUploadCloudFill}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Upload History</Heading>
                            <Text fontSize={'sm'}>Here you can see all your uploaded documents</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', xl: 'flex-end' }} justify={'end'} spacing={4}>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<FaCloudUploadAlt />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={onUploadOpen}
                        >
                            Upload File
                        </Button>
                        <Button
                            size={'sm'}
                            leftIcon={<FaSync />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={FetchList}
                        >
                            Refresh
                        </Button>
                        <Button
                            size={'sm'}
                            leftIcon={<FaFilter />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => setShowFilters((v) => !v)}
                        >
                            Filter
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align={{ md: 'center' }}>
                    <InputGroup size={'sm'} maxW={{ md: '280px' }}>
                        <InputLeftElement pointerEvents={'none'} children={<FaSearch color={'gray'} />} />
                        <Input placeholder={'Search uploads…'} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </InputGroup>
                    {showFilters && (
                        <Select size={'sm'} maxW={{ md: '220px' }} placeholder={'All statuses'} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value={'Uploaded'}>Uploaded</option>
                            <option value={'Processing'}>Processing</option>
                            <option value={'Processed'}>Processed</option>
                            <option value={'Error'}>Error</option>
                        </Select>
                    )}
                    {selected.size > 0 && <Text fontSize={'sm'} color={'primary.600'}>{selected.size} selected</Text>}
                </Stack>
                <TableContainer h={'sm'} overflowY={'scroll'}>
                    <Table size='sm'>
                        <Thead position={'sticky'} top={'0%'} zIndex={1} h={'50px'} bg={'primary.400'} alignItems={'flex-start'}>
                            <Tr>
                                <Th color={'white'} width={'40px'}>
                                    <Checkbox
                                        isChecked={allChecked}
                                        isIndeterminate={!allChecked && someChecked}
                                        onChange={toggleAll}
                                        colorScheme={'whiteAlpha'}
                                    />
                                </Th>
                                <Th
                                    color={'white'}
                                    isNumeric
                                >
                                    Doc ID
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    File Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Uploaded By
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Document Type
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Uploaded Date & Time
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Document Status
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Source
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    File Size
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Files
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Documents Pages
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {
                                filtered.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={11} textAlign={'center'}>
                                            {loading ? <Spinner size={'sm'} color={'primary.400'} /> : 'No Data Available'}
                                        </Td>
                                    </Tr>
                                ) :
                                filtered.map((item, i) => (
                                    <Tr key={i}>
                                        <Td>
                                            <Checkbox
                                                isChecked={selected.has(String(item.id))}
                                                onChange={() => toggleOne(String(item.id))}
                                            />
                                        </Td>
                                        <Td isNumeric>{item.id}</Td>
                                        <Td>{item.filename}</Td>
                                        <Td>{item.uploadedBy}</Td>
                                        <Td>{item.docType}</Td>
                                        <Td>{convertDate(item.uploadDateTime)}</Td>
                                        <Td>{item.status}</Td>
                                        <Td>{item.source}</Td>
                                        <Td>{item.fileSize}</Td>
                                        <Td>
                                            <IconButton
                                                aria-label="Download File"
                                                icon={<FaFile />}
                                                size={'md'}
                                                bg={'transparent'}
                                                color={'green.400'}
                                                rounded={'full'}
                                            />
                                        </Td>
                                        <Td>{item.documentsPages}</Td>
                                    </Tr>
                                ))
                            }
                        </Tbody>
                    </Table>
                </TableContainer>
                <Stack
                    direction={'row'}
                    justify={"space-between"}
                    align={'center'}
                >
                    <Text>Showing {items.length} of {pagination.totalRecords} results</Text>
                    <Stack direction={'row'} spacing={4}>
                        <Select defaultValue={10} onChange={(e) => setPaginationQuery({ page: 1, pageSize: parseInt(e.target.value) })}>
                            <option value={5}>5 Per Page</option>
                            <option value={10}>10 Per Page</option>
                            <option value={25}>25 Per Page</option>
                            <option value={50}>50 Per Page</option>
                        </Select>
                        <Stack direction={'row'}>
                            <IconButton
                                aria-label="Previous"
                                icon={<FaArrowLeft />}
                                rounded={'full'}
                                disabled={pagination.currentPage <= 1}
                                onClick={() => setPaginationQuery({ ...paginationQuery, page: pagination.currentPage - 1 })}
                            />
                            <IconButton
                                aria-label="Next"
                                icon={<FaArrowRight />}
                                rounded={'full'}
                                disabled={pagination.currentPage >= pagination.totalPages}
                                onClick={() => setPaginationQuery({ ...paginationQuery, page: pagination.currentPage + 1 })}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack >
    )
}